import { SESSION_CORRELATION_ID } from '@src/utils/general';
import {
  FULFILLMENT_CODE_UNKNOWN,
  FULFILLMENT_TYPE,
  FULFILLMENT_TYPE_CODE,
} from '@src/constants/status';
import { ItemToReturn, ShippingInfoDict } from '@src/models/returns';
import { useReturnStore } from '@src/stores/returnStore';
import { useSummaryStore } from '@src/stores/summaryStore';
import {
  ReturnsContext,
  ReturnsFlowStepEvent,
  ReturnsFlowStepName,
  ReturnsBusinessErrorEvent,
  ReturnsOutcomeEvent,
  ReturnsOutcomeResult,
  ReturnsOutcomeStep,
  ReturnsGate,
  ReturnsGateEvent,
  ReturnsGateResultValue,
  trackReturnsFlowStep,
  trackReturnsBusinessError,
  trackReturnsOutcome,
  trackReturnsGateResult,
} from '@src/utils/returns-instrumentation';

/**
 * Canonical code order, derived from FULFILLMENT_TYPE_CODE's own insertion order so the
 * two can't drift. Codes absent from the map (UNKNOWN) rank last. Computed once, not
 * per PageAction.
 */
const CANONICAL_CODE_ORDER = Object.values(FULFILLMENT_TYPE_CODE);

const rankCode = (code: string): number => {
  const index = CANONICAL_CODE_ORDER.indexOf(code);
  return index === -1 ? CANONICAL_CODE_ORDER.length : index;
};

/**
 * Joins selectedItems to the O&P deliveryDictionary by lineItemId. An item with no entry
 * contributes UNKNOWN rather than being dropped, so mapping gaps get their own facet
 * slice; undefined when nothing is selected. Exported so it can be tested directly.
 */
export const encodeFulfillmentMix = (
  selectedItems: Pick<ItemToReturn, 'lineItemId'>[] | undefined,
  deliveryDictionary: ShippingInfoDict | undefined,
): string | undefined => {
  const codes = [
    ...new Set(
      (selectedItems ?? []).map((item) => {
        const rawType =
          deliveryDictionary?.[item.lineItemId]?.fulfillmentType ?? '';
        // Own-property check rather than a bare index: a value like 'constructor'
        // would otherwise resolve up the prototype chain and put a function on the facet.
        return Object.prototype.hasOwnProperty.call(
          FULFILLMENT_TYPE_CODE,
          rawType,
        )
          ? FULFILLMENT_TYPE_CODE[rawType as FULFILLMENT_TYPE]
          : FULFILLMENT_CODE_UNKNOWN;
      }),
    ),
  ];
  if (!codes.length) return undefined;
  codes.sort(
    (left, right) =>
      rankCode(left) - rankCode(right) || left.localeCompare(right),
  );
  return codes.join('+');
};

/**
 * Builds the shared non-PII context from the Zustand stores at *call time*
 * (getState() is fresh even inside async .then/.catch callbacks), so the emit
 * helpers below work uniformly in React components, hooks, and the non-React
 * api-helpers layer — no React hook required.
 */
export const getReturnsContext = (): ReturnsContext => {
  const returns = useReturnStore.getState();
  const summary = useSummaryStore.getState();
  // The selected method(s) live per-item on item.returnMethod (mirrored into
  // newFlowStore.chosenReturnMethods) — NOT on a returnStore scalar. Derive the mix
  // straight from selectedItems: it's the same canonical source and avoids a
  // circular import with newFlowStore (which imports the emit helpers from this
  // module). Dedup is inlined rather than reusing getUniqueMethods so this
  // stays independent of @src/utils/general, which many callers' tests mock.
  // Encode as a stable, sorted, '+'-joined string so the dashboard facets
  // cleanly (e.g. 'LORP+UPS'); leave it unset before any method is chosen.
  const methods = [
    ...new Set(
      (returns.selectedItems ?? [])
        .map((item) => item.returnMethod)
        .filter(Boolean),
    ),
  ] as string[];
  return {
    correlationId: SESSION_CORRELATION_ID,
    returnMethod: methods.length ? [...methods].sort().join('+') : undefined,
    fulfillmentType: encodeFulfillmentMix(
      returns.selectedItems,
      returns.orderDetailsV2?.deliveryDictionary,
    ),
    itemCount: returns.selectedItems?.length,
    isKeepIt: summary.isKeepIt,
    militaryExchange: returns.militaryExchange || undefined,
  };
};

/**
 * Event-specific fields only. Context keys come from getReturnsContext() and the
 * discriminator fields come from the explicit positional args, so both are omitted
 * from `extra` — a caller can't override them (and the positional args are spread
 * last as a runtime safeguard).
 */
type Extra<E, PositionalKey extends keyof E = never> = Omit<
  Partial<E>,
  keyof ReturnsContext | PositionalKey
>;

export const emitReturnsFlowStep = (
  step: ReturnsFlowStepName,
  extra: Extra<ReturnsFlowStepEvent, 'step'> = {},
): void => trackReturnsFlowStep({ ...getReturnsContext(), ...extra, step });

export const emitReturnsBusinessError = (
  errorCode: string,
  extra: Extra<ReturnsBusinessErrorEvent, 'errorCode'> = {},
): void =>
  trackReturnsBusinessError({ ...getReturnsContext(), ...extra, errorCode });

export const emitReturnsOutcome = (
  step: ReturnsOutcomeStep,
  result: ReturnsOutcomeResult,
  extra: Extra<ReturnsOutcomeEvent, 'step' | 'result'> = {},
): void =>
  trackReturnsOutcome({ ...getReturnsContext(), ...extra, step, result });

export const emitReturnsGate = (
  gate: ReturnsGate,
  result: ReturnsGateResultValue,
  extra: Extra<ReturnsGateEvent, 'gate' | 'result'> = {},
): void =>
  trackReturnsGateResult({ ...getReturnsContext(), ...extra, gate, result });
